import React, { forwardRef, useRef } from "react";
import { useTranslation } from "react-i18next";
import { composeRefs, useControlled } from "@exonn/hooks";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { Skeleton } from "src/components/Skeleton/Skeleton.tsx";
import { InputErrorMessage } from "../InputErrorMessage/InputErrorMessage.tsx";
import * as styles from "./TextArea.styles.ts";
import type { ChangeEvent, Ref } from "react";
import type { TextareaProps } from "./TextArea.types.ts";

import { loadTranslations } from "src/lib/i18next/loadTranslations.ts";
import { Flex } from "src/components/Flex.tsx";
import { Placeholders } from "src/components/InputPlaceholders/InputPlaceholders.tsx";
import { useTextareaInsertion } from "src/components/TextArea/useTextAreaInsertion.tsx";
import type { TextTemplatePlaceholder } from "@exonn/js-sdk";
import { InputLabel } from "src/components/InputLabel/InputLabel.tsx";

await loadTranslations(TranslationNamespaceEnum.TextAreaComponent);

const TextAreaComponent = (
  props: TextareaProps,
  ref: Ref<HTMLTextAreaElement>,
) => {
  const { t } = useTranslation();

  const {
    value: controlledValue,
    label,
    helperText,
    floatingError = false,
    placeholders = [],
    required,
    onSelectPlaceholder,
    placeholdersPlacement = "aboveLabel",
    rootGap = 15,
    defaultValue,
    error,
    resize = "none",
    placeholder = t("placeholder", {
      ns: TranslationNamespaceEnum.TextAreaComponent,
    }),
    maxLength,
    disabled,
    className,
    onChange,
    isLoading = false,
    ...rest
  } = props;

  const [value, setValue] = useControlled({
    controlled: controlledValue,
    default: defaultValue,
    name: "TextArea",
    state: "value",
  });

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
    onChange?.(e);
  };

  const inputRef = useRef<HTMLTextAreaElement>(null);

  const { saveCursor, restoreCursor, insertText } = useTextareaInsertion();

  const handleSelectPlaceholder = (placeholder: TextTemplatePlaceholder) => {
    onSelectPlaceholder?.(placeholder);

    if (!inputRef.current) return;

    const code =
      placeholder.type === "conditional"
        ? `{% if ${placeholder.value} %} ... {% endif %}`
        : `{{ ${placeholder.value} }}`;

    const newValue = insertText(inputRef.current, value ?? "", code);

    const syntheticEvent = {
      currentTarget: {
        value: newValue,
        name: (rest as any)?.name,
      },
      target: {
        value: newValue,
        name: (rest as any)?.name,
      },
    } as unknown as ChangeEvent<HTMLTextAreaElement>;
    handleChange?.(syntheticEvent);

    restoreCursor(inputRef.current);
  };

  const charCount = value ? value.length : 0;

  return (
    <Flex flexDirection="column" gap={`${rootGap}px`}>
      {placeholdersPlacement === "aboveLabel" && (
        <Placeholders
          disabled={disabled}
          onSelectPlaceholder={handleSelectPlaceholder}
          placeholders={placeholders}
        />
      )}
      <Skeleton isLoading={isLoading}>
        <div css={styles.rootContainer} className={className}>
          {label && (
            <InputLabel
              label={label}
              required={required}
              helperText={helperText}
            />
          )}
          {placeholdersPlacement === "underLabel" &&
            placeholders.length > 0 && (
              <Flex
                flexDirection="column"
                css={styles.placeholdersContainerUnderLabel}
              >
                <Placeholders
                  disabled={disabled}
                  onSelectPlaceholder={handleSelectPlaceholder}
                  placeholders={placeholders}
                />
              </Flex>
            )}

          <textarea
            ref={composeRefs(ref, inputRef)}
            css={styles.textareaStyles({ error, resize, disabled })}
            maxLength={maxLength}
            disabled={disabled}
            placeholder={placeholder}
            value={value}
            onSelect={(e) => saveCursor(e.currentTarget)}
            defaultValue={defaultValue}
            onChange={handleChange}
            {...rest}
          />

          {maxLength && !disabled && (
            <p css={styles.helperTextStyles}>
              {charCount}/{maxLength}
            </p>
          )}

          {error && (
            <InputErrorMessage floating={floatingError} message={error} />
          )}
        </div>
      </Skeleton>
    </Flex>
  );
};

export const TextArea = forwardRef(TextAreaComponent);
